'use client';

import React, { useState, useEffect } from 'react';
import { CustomCheckbox } from '../components/forms/CustomCheckbox';

interface TrainingProgram {
  id: string;
  title: string;
  level: 'junior' | 'middle' | 'lead';
  format: 'online' | 'offline' | 'corp';
  hours: number;
  price: number;
  cover: string;
  badges: string[];
  short: string;
  syllabus: string[];
  outcomes: string[];
  who: string[];
  dates: string[];
}

interface FilterState {
  q: string;
  format: string;
  level: string;
  dur: string;
  sort: string;
}

const TRAINING: TrainingProgram[] = [
  {
    id: 't1',
    title: 'RPO за 14 дней: от KPI до отчётности',
    level: 'middle',
    format: 'online',
    hours: 6,
    price: 9900,
    cover: 'https://placehold.co/800x480?text=RPO+Intensive',
    badges: ['CERT', 'LAB'],
    short: 'Интенсив по запуску RPO: воронка, KPI/SLA, отчётность, коммуникации.',
    syllabus: [
      'Диагностика и бриф: KPI/SLA',
      'Источники кандидатов и UTM',
      'Скрининг и интервью',
      'Слоты, явка, no-show',
      'Отчёты: Looker/Power BI',
      'Гарантийные периоды и замены',
    ],
    outcomes: ['Соберёте воронку за 2 недели', 'Настроите отчётность', 'Снизите CPH и Time-to-Hire'],
    who: ['HRD, руководители рекрутмента', 'Тимлиды RPO'],
    dates: ['2025-09-05 11:00–14:00 (MSK)', '2025-09-06 11:00–14:00 (MSK)'],
  },
  {
    id: 't2',
    title: 'Практикум ATS & BI: дашборды рекрутера',
    level: 'middle',
    format: 'online',
    hours: 8,
    price: 12900,
    cover: 'https://placehold.co/800x480?text=ATS+%26+BI',
    badges: ['LAB', 'CERT'],
    short: 'Построение отчётов по воронке, конверсиям и времени цикла. Экспорт/ETL.',
    syllabus: ['Схема данных ATS', 'ETL и чистка', 'Дашборды: конверсии', 'TTH и CPH', 'Сегментация источников', 'Алерты KPI'],
    outcomes: ['Готовый шаблон дашборда', 'Экспорт данных', 'Авто-алерты KPI'],
    who: ['Аналитики рекрутмента', 'Руководители групп'],
    dates: ['2025-09-12 10:00–14:00 (MSK)', '2025-09-13 10:00–14:00 (MSK)'],
  },
  {
    id: 't3',
    title: 'L1/L2 поддержка: запуск и контроль качества',
    level: 'junior',
    format: 'offline',
    hours: 4,
    price: 7900,
    cover: 'https://placehold.co/800x480?text=L1%2FL2+Support',
    badges: ['CERT'],
    short: 'Скрипты, SLA, QA-оценка и база знаний. Практика на кейсах.',
    syllabus: ['Скрипты и triage', 'SLA и эскалация', 'QA-чек-листы', 'База знаний', 'Нагрузочное планирование'],
    outcomes: ['Повысите FCR и CSAT', 'Внедрите QA', 'Сократите эскалации'],
    who: ['Супервизоры L1/L2', 'Новички саппорта'],
    dates: ['2025-09-18 11:00–15:00 (СПб)'],
  },
  {
    id: 't4',
    title: 'Soft-skills рекрутера: интервью по компетенциям',
    level: 'junior',
    format: 'online',
    hours: 3,
    price: 4900,
    cover: 'https://placehold.co/800x480?text=Soft+Skills',
    badges: [],
    short: 'Структура интервью, вопросы STAR/BEI, обратная связь кандидату.',
    syllabus: ['STAR & BEI', 'Анти-bias', 'Фидбек', 'Этика', 'Калибровка оценок'],
    outcomes: ['Рост качества интервью', 'Единые критерии', 'Позитивный candidate experience'],
    who: ['Рекрутеры уровня Junior', 'Хайринг-менеджеры'],
    dates: ['2025-08-30 12:00–15:00 (MSK)'],
  },
];

const durBand = (h: number) => {
  if (h <= 3) return '1-3';
  if (h <= 8) return '4-8';
  return '9+';
};
const money = (n: number) => (n === 0 ? 'Бесплатно' : `${n.toLocaleString('ru-RU')} ₽`);

const perPage = 6;

export default function Home() {
  const [filters, setFilters] = useState<FilterState>({
    q: '',
    format: '',
    level: '',
    dur: '',
    sort: 'new',
  });
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTraining, setSelectedTraining] = useState<TrainingProgram | null>(null);
  const [isConsentCheckedModal, setIsConsentCheckedModal] = useState(false);
  const [isConsentCheckedCorp, setIsConsentCheckedCorp] = useState(false);
  const [modalForm, setModalForm] = useState({
    name: '',
    email: '',
    phone: '',
    tariff: 'Тариф: стандарт',
    comment: '',
  });
  const [corporateForm, setCorporateForm] = useState({
    company: '',
    email: '',
    contactPerson: '',
    phone: '',
    subject: 'Тема: RPO/Подбор',
    format: 'Формат: онлайн',
    comment: '',
  });
  const [modalStatus, setModalStatus] = useState<string | null>(null);
  const [corpStatus, setCorpStatus] = useState<string | null>(null);
  const [modalSubmitting, setModalSubmitting] = useState(false);
  const [corpSubmitting, setCorpSubmitting] = useState(false);

  const filteredPrograms = TRAINING.filter((t) => {
    const q = filters.q.toLowerCase();
    const hay = (t.title + ' ' + (t.short || '') + ' ' + (t.syllabus || []).join(' ')).toLowerCase();
    return (
      (!q || hay.includes(q)) &&
      (!filters.format || t.format === filters.format) &&
      (!filters.level || t.level === filters.level) &&
      (!filters.dur || durBand(t.hours) === filters.dur)
    );
  }).sort((a, b) => {
    switch (filters.sort) {
      case 'new':
        return b.id.localeCompare(a.id);
      case 'popular':
        return (b.badges?.length || 0) - (a.badges?.length || 0);
      case 'priceAsc':
        return a.price - b.price;
      case 'priceDesc':
        return b.price - a.price;
      default:
        return 0;
    }
  });

  const totalPages = Math.max(1, Math.ceil(filteredPrograms.length / perPage));
  const pagePrograms = filteredPrograms.slice((page - 1) * perPage, page * perPage);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { id, value } = e.target;
    setFilters((prev) => ({ ...prev, [id]: value }));
    setPage(1);
  };

  const openModal = (id: string) => {
    const program = TRAINING.find((t) => t.id === id);
    if (program) {
      setSelectedTraining(program);
      setModalOpen(true);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedTraining(null);
    setModalForm({
      name: '',
      email: '',
      phone: '',
      tariff: 'Тариф: стандарт',
      comment: '',
    });
    setIsConsentCheckedModal(false);
    setModalStatus(null);
    setModalSubmitting(false);
  };

  const handleEnroll = (id: string) => {
    openModal(id);
    setTimeout(() => {
      document.getElementById('modal-form')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleModalFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setModalForm((prev) => ({ ...prev, [name]: value }));
    if (modalStatus) setModalStatus(null);
  };

  const handleCorporateFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setCorporateForm((prev) => ({ ...prev, [name]: value }));
    if (corpStatus) setCorpStatus(null);
  };

  const handleModalConsentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsConsentCheckedModal(e.target.checked);
    if (e.target.checked && modalStatus?.includes('согласитесь с обработкой')) {
      setModalStatus(null);
    }
  };

  const handleCorpConsentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsConsentCheckedCorp(e.target.checked);
    if (e.target.checked && corpStatus?.includes('согласитесь с обработкой')) {
      setCorpStatus(null);
    }
  };

  const handleModalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTraining) return;

    if (!isConsentCheckedModal) {
      setModalStatus('Пожалуйста, согласитесь с обработкой персональных данных.');
      return;
    }

    setModalSubmitting(true);
    setModalStatus('Отправка...');

    const STRAPI_URL = `${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/application-for-trainings`;

    const payload = {
      data: {
        ...modalForm,
        trainingTitle: selectedTraining.title,
        trainingPrice: selectedTraining.price,
        source: 'Landing Page Form',
      },
    };

    try {
      const response = await fetch(STRAPI_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setModalStatus('Спасибо! Ваша заявка отправлена.');
        closeModal();
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error?.message || 'Неизвестная ошибка сервера.';
        setModalStatus(`Ошибка: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Ошибка сети:', error);
      setModalStatus('Не удалось подключиться к серверу. Пожалуйста, проверьте ваше соединение.');
    } finally {
      setModalSubmitting(false);
    }
  };

  const handleCorpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isConsentCheckedCorp) {
      setCorpStatus('Пожалуйста, согласитесь с обработкой персональных данных.');
      return;
    }

    setCorpSubmitting(true);
    setCorpStatus('Отправка...');

    const STRAPI_URL = `${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/enrollments`;

    const payload = {
      data: {
        company: corporateForm.company,
        email: corporateForm.email,
        contactPerson: corporateForm.contactPerson,
        phone: corporateForm.phone,
        subject: corporateForm.subject,
        format: corporateForm.format,
        comment: corporateForm.comment,
        source: 'Corporate Training Form',
        formName: 'Обучение INTERIM',
      },
    };

    try {
      const response = await fetch(STRAPI_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setCorpStatus('Спасибо! Ваша заявка отправлена.');
        setCorporateForm({
          company: '',
          email: '',
          contactPerson: '',
          phone: '',
          subject: 'Тема: RPO/Подбор',
          format: 'Формат: онлайн',
          comment: '',
        });
        setIsConsentCheckedCorp(false);
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error?.message || 'Неизвестная ошибка сервера.';
        setCorpStatus(`Ошибка: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Ошибка сети:', error);
      setCorpStatus('Не удалось подключиться к серверу. Пожалуйста, проверьте ваше соединение.');
    } finally {
      setCorpSubmitting(false);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [filters]);

  return (
    <>
      <style jsx global>{`
        :root {
          --accent: #ffc300;
          --card: rgba(255, 255, 255, 0.08);
          --chip: rgba(255, 255, 255, 0.14);
        }
        .card {
          background: var(--card);
          backdrop-filter: blur(8px);
        }
        .chip {
          background: var(--chip);
        }
        .btn {
          border-radius: 0.75rem;
          padding: 0.7rem 1rem;
          font-weight: 700;
        }
        .btn-accent {
          background: var(--accent);
          color: #102a43;
        }
        .btn-ghost {
          background: rgba(255, 255, 255, 0.1);
        }
        .btn-ghost:hover {
          background: rgba(255, 255, 255, 0.2);
        }
        .tag {
          background: rgba(255, 255, 255, 0.12);
          padding: 0.25rem 0.6rem;
          border-radius: 999px;
          font-size: 0.75rem;
        }
        .badge {
          padding: 0.2rem 0.5rem;
          border-radius: 0.5rem;
          font-size: 0.7rem;
        }
        .badge-cert {
          background: rgba(34, 197, 94, 0.25);
          color: #bbf7d0;
        }
        .badge-lab {
          background: rgba(59, 130, 246, 0.25);
          color: #bfdbfe;
        }
        .modal {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.55);
          display: none;
          align-items: center;
          justify-content: center;
          z-index: 50;
        }
        .modal.open {
          display: flex;
        }
      `}</style>

      <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-gray-100 antialiased">
        <header
          className="px-6 pt-32 pb-16 md:pt-40 md:pb-20"
          style={{
            background:
              'linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.25)), radial-gradient(1200px 600px at 70% -10%, rgba(255,255,255,.08), transparent)',
          }}
        >
          <div className="max-w-7xl mx-auto">
            <nav className="mb-6 flex flex-wrap gap-3 text-sm">
              <a href="#catalog" className="chip px-3 py-1 rounded-full hover:bg-white/25">
                Каталог программ
              </a>
              <a href="#filters" className="chip px-3 py-1 rounded-full hover:bg-white/25">
                Фильтры
              </a>
              <a href="#corp" className="chip px-3 py-1 rounded-full hover:bg-white/25">
                Корпоративное
              </a>
              <a href="#faq" className="chip px-3 py-1 rounded-full hover:bg-white/25">
                FAQ
              </a>
            </nav>

            <h1 className="text-4xl md:text-6xl font-extrabold mb-4">Обучение INTERIM</h1>
            <p className="max-w-3xl text-lg md:text-xl text-gray-200">
              Практические курсы для рекрутеров и операционных команд: подбор персонала, работа с ATS и аналитикой, L1/L2 поддержка, управление воронкой и гибкие навыки. Форматы: онлайн, очно, корпоративно.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#catalog" className="btn btn-ghost">
                Смотреть программы
              </a>
              <a href="#corp" className="btn btn-accent">
                Запросить корпоративное
              </a>
            </div>
          </div>
        </header>

        <main className="px-6 py-12 md:py-16 max-w-7xl mx-auto space-y-16">
          <section id="filters" aria-label="Фильтры" className="scroll-mt-32">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Найдите подходящее обучение</h2>
            <div className="grid gap-3 md:grid-cols-6">
              <input
                id="q"
                className="rounded-xl px-4 py-3 text-gray-900 md:col-span-2"
                placeholder="Поиск по названию/описанию…"
                value={filters.q}
                onChange={handleFilterChange}
              />

              <select
                id="format"
                className="rounded-xl px-4 py-3 text-gray-900"
                value={filters.format}
                onChange={handleFilterChange}
              >
                <option value="">Формат: все</option>
                <option value="online">Онлайн</option>
                <option value="offline">Очно</option>
                <option value="corp">Корпоративное</option>
              </select>

              <select
                id="level"
                className="rounded-xl px-4 py-3 text-gray-900"
                value={filters.level}
                onChange={handleFilterChange}
              >
                <option value="">Уровень: все</option>
                <option value="junior">Младший</option>
                <option value="middle">Средний</option>
                <option value="lead">Ведущий</option>
              </select>

              <select
                id="dur"
                className="rounded-xl px-4 py-3 text-gray-900"
                value={filters.dur}
                onChange={handleFilterChange}
              >
                <option value="">Длительность</option>
                <option value="1-3">1–3 часа</option>
                <option value="4-8">4–8 часов</option>
                <option value="9+">9+ часов</option>
              </select>

              <select
                id="sort"
                className="rounded-xl px-4 py-3 text-gray-900"
                value={filters.sort}
                onChange={handleFilterChange}
              >
                <option value="new">Сначала новые</option>
                <option value="popular">По популярности</option>
                <option value="priceAsc">Цена ↑</option>
                <option value="priceDesc">Цена ↓</option>
              </select>

              <div
                id="stats"
                className="card rounded-xl px-4 py-3 text-sm text-gray-200 hover:scale-100 transition-none"
              >
                Программ в каталоге: {TRAINING.length}
              </div>

            </div>
          </section>

          <section id="catalog" aria-label="Каталог программ" className="scroll-mt-32">
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <h2 className="text-3xl md:text-4xl font-bold">Каталог программ</h2>
              <span id="count" className="text-sm text-gray-300">
                Найдено: {filteredPrograms.length}
              </span>
            </div>

            <div id="grid" className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {pagePrograms.length > 0 ? (
                pagePrograms.map((t) => (
                  <article key={t.id} className="card rounded-2xl overflow-hidden flex flex-col">
                    <img
                      src={t.cover}
                      alt=""
                      className="w-full h-44 object-cover"
                      onError={(e) => {
                        e.currentTarget.src = 'https://placehold.co/800x480?text=Training';
                      }}
                    />
                    <div className="p-5 flex-1 flex flex-col">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="text-xl font-semibold">{t.title}</h3>
                          <div className="mt-1 text-xs flex flex-wrap gap-2">
                            <span className="tag">
                              {t.format === 'online' ? 'Онлайн' : t.format === 'offline' ? 'Очно' : 'Корпоративное'}
                            </span>
                            <span className="tag">{t.level}</span>
                            <span className="tag">{t.hours} ч</span>
                          </div>
                        </div>
                        <div className="text-xs flex flex-col items-end gap-1">
                          {t.badges?.includes('CERT') && <span className="badge badge-cert">Сертификат</span>}
                          {t.badges?.includes('LAB') && <span className="badge badge-lab">Практикум</span>}
                        </div>
                      </div>

                      <p className="text-sm text-gray-200 mt-3 line-clamp-3">{t.short || ''}</p>

                      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                        {(t.syllabus || []).slice(0, 3).map((s, i) => (
                          <span key={i} className="tag">
                            {s}
                          </span>
                        ))}
                        <span className="tag">Цена: {money(t.price)}</span>
                      </div>

                      <div className="mt-4 flex items-center justify-between">
                        <button className="btn btn-ghost text-sm" onClick={() => openModal(t.id)}>
                          Подробнее
                        </button>
                        <button className="btn btn-accent text-sm" onClick={() => handleEnroll(t.id)}>
                          Записаться
                        </button>
                      </div>
                    </div>
                  </article>
                ))
              ) : (
                <p id="empty" className="text-gray-300">
                  Ничего не найдено — измените фильтры.
                </p>
              )}
            </div>

            <div id="pager" className="mt-6 flex flex-wrap items-center gap-2">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((i) => (
                <button
                  key={i}
                  className={`px-3 py-2 rounded-lg ${i === page ? 'btn-accent' : 'btn-ghost'}`}
                  onClick={() => setPage(i)}
                >
                  {i}
                </button>
              ))}
            </div>
          </section>

          <section
            id="corp"
            className="card rounded-2xl p-6 scroll-mt-32 hover:scale-100  transition-none"
          >
            <h3 className="text-2xl font-bold">Корпоративное обучение</h3>
            <p className="text-sm text-gray-200 mt-1">
              Под запрос: цели, роли, KPI, сценарии интервью, материалы в вашем брендбуке. Возможна сертификация сотрудников.
            </p>
            <form className="mt-4 grid gap-3 md:grid-cols-2" onSubmit={handleCorpSubmit}>
              <input
                name="company"
                value={corporateForm.company}
                onChange={handleCorporateFormChange}
                className="rounded-xl px-4 py-3 text-gray-900"
                placeholder="Компания*"
                required
              />
              <input
                name="email"
                value={corporateForm.email}
                onChange={handleCorporateFormChange}
                className="rounded-xl px-4 py-3 text-gray-900"
                placeholder="E-mail*"
                type="email"
                required
              />
              <input
                name="contactPerson"
                value={corporateForm.contactPerson}
                onChange={handleCorporateFormChange}
                className="rounded-xl px-4 py-3 text-gray-900"
                placeholder="Контактное лицо*"
                required
              />
              <input
                name="phone"
                value={corporateForm.phone}
                onChange={handleCorporateFormChange}
                className="rounded-xl px-4 py-3 text-gray-900"
                placeholder="Телефон*"
                pattern="^[0-9+()\\-\\s]{6,}$"
                required
              />
              <select
                name="subject"
                value={corporateForm.subject}
                onChange={handleCorporateFormChange}
                className="rounded-xl px-4 py-3 text-gray-900"
              >
                <option>Тема: RPO/Подбор</option>
                <option>Тема: ATS/BI</option>
                <option>Тема: L1/L2 поддержка</option>
                <option>Тема: Soft-skills</option>
              </select>
              <select
                name="format"
                value={corporateForm.format}
                onChange={handleCorporateFormChange}
                className="rounded-xl px-4 py-3 text-gray-900"
              >
                <option>Формат: онлайн</option>
                <option>Формат: очно</option>
                <option>Формат: смешанный</option>
              </select>
              <textarea
                name="comment"
                value={corporateForm.comment}
                onChange={handleCorporateFormChange}
                className="rounded-xl px-4 py-3 text-gray-900 md:col-span-2"
                rows={4}
                placeholder="Цели, желаемые даты, аудитория"
              />
              <CustomCheckbox
                name="consent"
                isChecked={isConsentCheckedCorp}
                onChange={handleCorpConsentChange}
                link="/privacy"
                textColorClass="text-gray-400"
                linkColorClass="text-[#FF7C22]"
                className="md:col-span-2"
              />
              <button className="btn btn-accent md:col-span-2" disabled={corpSubmitting}>
                {corpSubmitting ? 'Отправка...' : 'Запросить программу'}
              </button>
              {corpStatus && (
                <div
                  className={`mt-2 text-center md:col-span-2 text-sm ${corpStatus.startsWith('Ошибка') || corpStatus.includes('Не удалось') ? 'text-red-400' : 'text-green-300'
                    }`}
                >
                  {corpStatus}
                </div>
              )}
            </form>
          </section>

          <section id="faq" className="scroll-mt-32">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">FAQ</h2>
            <div className="space-y-3">
              <details className="card rounded-xl p-4 hover:scale-100 transition-none">
                <summary className="cursor-pointer font-semibold">Есть ли сертификат?</summary>
                <p className="mt-2 text-sm text-gray-200">
                  Да, по итогам выдаём именной сертификат INTERIM (по запросу — с тестированием).
                </p>
              </details>

              <details className="card rounded-xl p-4 hover:scale-100 transition-none">
                <summary className="cursor-pointer font-semibold">Можно оплатить по счёту?</summary>
                <p className="mt-2 text-sm text-gray-200">
                  Да. Для юрлиц — безналичный расчёт; для физлиц — онлайн-оплата.
                </p>
              </details>
            </div>
          </section>
        </main>

        <div
          id="modal"
          className={`modal ${modalOpen ? 'open' : ''}`}
          aria-hidden={!modalOpen}
          role="dialog"
          aria-label="Программа курса"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          {selectedTraining && (
            <div className="card rounded-2xl p-6 max-w-3xl w-[92%] relative">
              <button
                id="close"
                className="absolute right-3 top-3 px-2 py-1 rounded-lg btn-ghost"
                aria-label="Закрыть"
                onClick={closeModal}
              >
                ✕
              </button>
              <article>
                <div className="grid gap-4 md:grid-cols-2">
                  <img
                    src={selectedTraining.cover}
                    alt=""
                    className="rounded-xl w-full h-56 object-cover"
                  />
                  <div>
                    <h2 className="text-2xl font-bold">{selectedTraining.title}</h2>
                    <div className="mt-2 text-xs flex flex-wrap gap-2">
                      <span className="tag">
                        {selectedTraining.format === 'online'
                          ? 'Онлайн'
                          : selectedTraining.format === 'offline'
                            ? 'Очно'
                            : 'Корпоративное'}
                      </span>
                      <span className="tag">{selectedTraining.level}</span>
                      <span className="tag">{selectedTraining.hours} ч</span>
                      {selectedTraining.badges.includes('CERT') && <span className="tag">Сертификат</span>}
                      {selectedTraining.badges.includes('LAB') && <span className="tag">Практикум</span>}
                    </div>
                    <p className="text-sm text-gray-200 mt-3">{selectedTraining.short || ''}</p>

                    <h3 className="font-semibold mt-4">Результаты</h3>
                    <ul className="list-disc ml-6 text-sm text-gray-200">
                      {(selectedTraining.outcomes || []).map((o, i) => (
                        <li key={i}>{o}</li>
                      ))}
                    </ul>

                    <h3 className="font-semibold mt-4">Кому подходит</h3>
                    <ul className="list-disc ml-6 text-sm text-gray-200">
                      {(selectedTraining.who || []).map((o, i) => (
                        <li key={i}>{o}</li>
                      ))}
                    </ul>

                    <h3 className="font-semibold mt-4">Расписание</h3>
                    <ul className="list-disc ml-6 text-sm text-gray-200">
                      {(selectedTraining.dates || []).map((o, i) => (
                        <li key={i}>{o}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                <form
                  id="modal-form"
                  className="mt-6 card rounded-2xl p-6 grid gap-3 md:grid-cols-2"
                  onSubmit={handleModalSubmit}
                >
                  <input
                    name="name"
                    value={modalForm.name}
                    onChange={handleModalFormChange}
                    className="rounded-xl px-4 py-3 text-gray-900"
                    placeholder="Имя*"
                    required
                  />
                  <input
                    name="email"
                    value={modalForm.email}
                    onChange={handleModalFormChange}
                    className="rounded-xl px-4 py-3 text-gray-900"
                    placeholder="E-mail*"
                    type="email"
                    required
                  />
                  <input
                    name="phone"
                    value={modalForm.phone}
                    onChange={handleModalFormChange}
                    className="rounded-xl px-4 py-3 text-gray-900"
                    placeholder="Телефон*"
                    pattern="^[0-9+()\\-\\s]{6,}$"
                    required
                  />
                  <select
                    name="tariff"
                    value={modalForm.tariff}
                    onChange={handleModalFormChange}
                    className="rounded-xl px-4 py-3 text-gray-900"
                    required
                  >
                    <option>Тариф: стандарт</option>
                    <option>Тариф: с ментором</option>
                    <option>Тариф: корпоративный</option>
                  </select>
                  <textarea
                    name="comment"
                    value={modalForm.comment}
                    onChange={handleModalFormChange}
                    className="rounded-xl px-4 py-3 text-gray-900 md:col-span-2"
                    rows={3}
                    placeholder="Комментарий (необязательно)"
                  />
                  <div className="text-xs text-gray-300 md:col-span-2">
                    Курс: <b>{selectedTraining.title}</b> • Стоимость: <b>{money(selectedTraining.price)}</b>
                  </div>
                  <CustomCheckbox
                    name="consent"
                    isChecked={isConsentCheckedModal}
                    onChange={handleModalConsentChange}
                    link="/privacy"
                    textColorClass="text-gray-400"
                    linkColorClass="text-[#FF7C22]"
                    className="md:col-span-2"
                  />
                  <button className="btn btn-accent md:col-span-2" disabled={modalSubmitting}>
                    {modalSubmitting ? 'Отправка...' : 'Отправить заявку'}
                  </button>
                  {modalStatus && (
                    <div
                      className={`mt-2 text-center md:col-span-2 text-sm ${modalStatus.startsWith('Ошибка') || modalStatus.includes('Не удалось') ? 'text-red-400' : 'text-green-300'
                        }`}
                    >
                      {modalStatus}
                    </div>
                  )}
                </form>
              </article>
            </div>
          )}
        </div>
      </div>
    </>
  );
}